import { useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

/**
 * useApi — the convenience read hook, now backed by React Query.
 *
 * The public contract is unchanged from the pre-RQ version so existing callers
 * need no edits. Internally it delegates to `useQuery`, which gives callers the
 * shared cache, request dedup, and `invalidateQueries` reactivity for free.
 *
 *   const { data, loading, error, refetch } = useApi(
 *     () => apiClient.listWorkProjects().then(r => r.items),
 *     [],
 *   )
 *
 * Sharing a cache entry: pass `options.queryKey` (from `@/lib/queryKeys`) so
 * that a mutation elsewhere can `invalidateQueries` this read. Without a
 * queryKey, the hook gets a per-instance key (isolated — matches old behavior).
 *
 * NOTE: when no `queryKey` is given, `deps` are folded into the auto key, so the
 * query refetches when deps change (same contract as the old useEffect deps).
 * Deps must therefore be serializable primitives — which all current callers use.
 */

export interface UseApiOptions<T> {
  /** Skip the fetch entirely (e.g. waiting on a route param). Default false. */
  skip?: boolean
  /** Seed value rendered before the first fetch resolves. Default null. */
  initialData?: T | null
  /**
   * Override the timestamp React Query assigns to initialData. Offline seeds
   * should render immediately but still revalidate when connectivity exists.
   */
  initialDataUpdatedAt?: number
  /** Called with the result of a successful fetch. */
  onSuccess?: (data: T) => void
  /** Called with the error of a failed fetch. */
  onError?: (error: Error) => void
  /**
   * Shared, invalidatable cache key (from `@/lib/queryKeys`). Omit for an
   * isolated per-instance cache entry (legacy behavior).
   */
  queryKey?: readonly unknown[]
}

export interface UseApiResult<T> {
  data: T | null
  loading: boolean
  error: Error | null
  /** Re-run the fetcher on demand (e.g. a retry button, or after a mutation). */
  refetch: () => Promise<void>
  /** Set the local cached data directly — for optimistic updates. */
  setData: (updater: T | null | ((prev: T | null) => T | null)) => void
}

let _uid = 0

export function useApi<T>(
  fetcher: () => Promise<T>,
  deps: React.DependencyList = [],
  options: UseApiOptions<T> = {},
): UseApiResult<T> {
  const { skip = false, initialData = null, initialDataUpdatedAt, onSuccess, onError, queryKey } = options

  // Stable per-instance id for the fallback key (callers without a queryKey).
  const idRef = useRef<string>('')
  if (!idRef.current) idRef.current = `useApi:${++_uid}`

  // Keep the latest fetcher without forcing it into the key.
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher

  const key = queryKey ?? [idRef.current, ...(deps as unknown[])]

  const qc = useQueryClient()

  const query = useQuery<T, Error>({
    queryKey: key as unknown[],
    queryFn: () => fetcherRef.current(),
    enabled: !skip,
    initialData: initialData ?? undefined,
    initialDataUpdatedAt,
  })

  // v5 removed onSuccess/onError from useQuery — replicate via effects.
  const onSuccessRef = useRef(onSuccess)
  onSuccessRef.current = onSuccess
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  useEffect(() => {
    if (query.isSuccess && query.data !== undefined) {
      onSuccessRef.current?.(query.data as T)
    }
    // Fire when a fresh success lands.
  }, [query.isSuccess, query.dataUpdatedAt])

  useEffect(() => {
    if (query.isError && query.error) {
      onErrorRef.current?.(query.error)
    }
  }, [query.isError, query.errorUpdatedAt])

  return {
    data: (query.data ?? null) as T | null,
    loading: query.isLoading,
    error: (query.error ?? null) as Error | null,
    refetch: async () => {
      await query.refetch()
    },
    setData: (updater) => {
      qc.setQueryData<T | null>(
        key as unknown[],
        updater as T | null | ((prev: T | null | undefined) => T | null),
      )
    },
  }
}
